import Head from 'next/head';
import { ReactElement, useEffect, useState } from 'react';
import { Section } from '../components/Website';
import AppHeaderLayout from '@root/components/App/Layouts/AppHeaderLayout';
import Stl from '@root/lib/Stl';
import style from '@app/Dashboard/Listings/Edit/EditListingNavigation.module.scss';
import TermsStyles from '@app/Dashboard/Terms/Terms.module.scss';
const st = (new Stl()).bind(style);

import { basePhotoUrl } from '@global/constants';

export default function TermsAndConditions(): ReactElement {
  const anchors = [];
  const [ renderAside, setRenderAside ] = useState([]);
  const [ activeItem, setActiveItem ] = useState(null);

  if (process.browser) {
    Array.from(document.getElementsByClassName(TermsStyles.anchor)).forEach((element: HTMLElement) => {
      anchors.push({ id: element.id, text: element.firstChild.nodeValue });
    });
  }

  useEffect(() => {
    setRenderAside(anchors.map((anchor, index) =>
      <div className={ TermsStyles['page-menu-container'] } key={ `${ index }-container` } >
        <a
          className={ st`navigation-item ${ TermsStyles['page-menu-item'] } ${ anchor.id === activeItem && 'navigation-item--active' }` }
          key={ index }
          href={ `#${ anchor.id }` }
          onClick={ () => setActiveItem(anchor.id) }
        >
          {anchor.text}
        </a>
      </div>
    ));
  }, [ anchors.length, activeItem ]);

  return (
    <>
      <Head>
        <title>YourDesq | Terms and Conditions</title>
        <meta property="og:image" content={ basePhotoUrl } />
      </Head>

      <main className={ TermsStyles['page-main-container'] }>
        <aside className={ st`navigation-panel` }>
          {renderAside}
        </aside>
        <Section>
          <div className="container">
            <div className="grid">
              <div className="col-m8 push-m2">
                <div className={ TermsStyles['page-intro'] }>
                  <h3 className={ TermsStyles.anchor } id="algemene_voorwaarden">Algemene voorwaarden</h3>
                  <div>Laatst gewijzigd: 16 april 2021</div>
                  <div>Dank voor het gebruikmaken van YourDesq!</div>
                </div>
                <div className="page-section">
                  <p>
                    Deze algemene voorwaarden <strong>(&quot;Algemene Voorwaarden&quot;)</strong> zijn een
                    bindende juridische overeenkomst tussen u en YourDesq, welke uw gebruik van de websites,
                    applicaties en andere aanbiedingen van YourDesq (gezamenlijk het <strong>&quot;YourDesq Platform&quot;</strong>)
                    regelt. Indien er in deze Algemene Voorwaarden wordt gesproken over &quot;YourDesq&quot;,
                    &quot;wij&quot;, &quot;ons&quot;, of &quot;onze&quot; verwijst dit naar Desq B.V.
                    (KvK-nummer: 81021453), statutair gevestigd te Amsterdam.
                  </p>
                </div>
                <div className="page-section">
                  <p>
                    YourDesq kan deze Algemene Voorwaarden op elk moment wijzigen. Wanneer wij wijzigingen
                    aanbrengen in deze Algemene Voorwaarden, zullen wij de herziene voorwaarden op het YourDesq
                    Platform plaatsen en de laatst gewijzigd-datum bovenaan deze Algemene Voorwaarden bijwerken.
                    Wij zullen u ook op de hoogte brengen van de voorgestelde wijzigingen via e-mail ten minste
                    dertig (30) dagen voor de datum waarop ze van kracht worden (de <strong>&quot;Wijzigingsupdate&quot;</strong>).
                    Als u het niet eens bent met de herziene Algemene Voorwaarden, kunt u uw overeenkomst
                    onmiddellijk beëindigen zoals bepaald in deze Algemene Voorwaarden. Wij zullen u in de
                    Wijzigingsupdate informeren over uw recht om uw overeenkomst te beëindigen. Als u de
                    overeenkomst niet beëindigt voor de datum waarop de herziene voorwaarden van kracht worden,
                    houdt uw voortdurende toegang tot of gebruik van het YourDesq Platform in dat u de herziene
                    voorwaarden accepteert.
                  </p>
                </div>
                <div className="page-section">
                  <p>
                    Het YourDesq Platform biedt een online dienst aan die professionals (gezamenlijk <strong>&quot;Leden&quot;</strong>
                    en afzonderlijk <strong>&quot;Lid&quot;</strong>) in staat stelt om een Werklocatie of professionele dienst
                    te publiceren, aan te bieden, te zoeken en te boeken. Leden die diensten publiceren
                    en aanbieden zijn <strong>&quot;Hosts&quot;</strong> en Leden die Werklocaties en diensten zoeken, boeken
                    of gebruiken zijn <strong>&quot;Gebruikers&quot;</strong>. Hosts bieden Werklocaties aan die kunnen worden
                    gecategoriseerd als hotels, Werklocaties, kantoorverdiepingen en kantoorgebouwen
                    (<strong>&quot;Werklocaties&quot;</strong>). Een Werklocatie bevat een of meer Werklocaties zoals kantoorruimte,
                    samenwerkingsruimte en evenementenruimte (<strong>&quot;Type Werkruimte&quot;</strong>), en een verscheidenheid
                    aan andere diensten (gezamenlijk <strong>&quot;Verhuurdiensten&quot;</strong> en elke Verhuurdienst die wordt
                    aangeboden <strong>&quot;YourDesq Advertentie&quot;</strong>).
                  </p>
                </div>
                <div className="page-section">
                  <p>
                    U moet een account registreren om toegang te krijgen tot en gebruik te maken van veel
                    functies van het YourDesq Platform, en u moet uw accountgegevens accuraat houden.
                    Als aanbieder van het YourDesq Platform, is YourDesq geen eigenaar van, heeft YourDesq
                    geen controle over, biedt YourDesq geen Verhuurdiensten aan noch beheert YourDesq Verhuurdiensten.
                    YourDesq is geen partij bij de contracten die direct worden gesloten tussen Hosts en Gebruikers,
                    noch valt YourDesq aan te merken als vastgoedmakelaar, verzekeraar, organisator of verkoper
                    van reispakketten onder Richtlijn (EU) 2015/2302. YourDesq treedt niet op als vertegenwoordiger
                    in enige hoedanigheid voor enig Lid, behalve zoals gespecificeerd in de betalingsvoorwaarden
                    (<strong>&quot;Betalingsvoorwaarden&quot;</strong>). Voor meer informatie over de rol van YourDesq wordt u verwezen
                    naar Artikel 5 van deze Algemene Voorwaarden
                  </p>
                </div>
                <div className="page-section">
                  <p>
                    Naast deze Algemene Voorwaarden hanteert YourDesq ook andere (beleids)voorwaarden die deze
                    Algemene Voorwaarden aanvullen, zoals het Privacybeleid, waarin het verzamelen en
                    gebruiken van persoonsgegevens wordt beschreven, en de Betalingsvoorwaarden, waarin
                    betaaldiensten worden geregeld die worden geleverd aan Leden door de YourDesq
                    betalingsentiteiten (<strong>&quot;YourDesq Betalingen&quot;</strong>).
                  </p>
                </div>
                <div className="page-section">
                  <p>
                    Als Verhuurder bent u verantwoordelijk voor het begrijpen en naleven
                    van alle wetten, regels, voorschriften en contracten met
                    derden die van toepassing zijn op de Verhuurdiensten.
                  </p>
                </div>
                {/* <div className="page-section">
                  <h3 className={ TermsStyles.anchor } id="inhoudsopgave">Inhoudsopgave</h3>
                  <div className="page-contents-table">
                    <div>
                      <ul>
                        <li>Algemene voorwaarden</li>
                        <li>Inhoudsopgave</li>
                        <li className={ TermsStyles.anchor } id="algemeen">ALGEMEEN</li>
                      </ul>
                      <ol>
                        <li className={ TermsStyles.anchor } id="definities">Definities</li>
                        <li>Toepasselijkheid van de Algemene Voorwaarden</li>
                        <li>Gebruik van het YourDesq Platform</li>
                        <li>Onderhoud van uw YourDesq Advertentie</li>
                        <li>Betalingsvoorwaarden</li>
                        <li>Commissie</li>
                        <li>Gedrag van Gebruikers</li>
                        <li>Privacy</li>
                        <li>Intellectueel Eigendom</li>
                        <li>Content</li>
                        <li>Aansprakelijkheid</li>
                        <li>Klachten en schadeclaims</li>
                        <li>Annulering</li>
                        <li>Beëindiging</li>
                        <li>Reviews</li>
                        <li>Disclaimer</li>
                        <li>Vrijwaring</li>
                        <li>Feedback</li>
                        <li>Toepasselijk recht en bevoegde rechter</li>
                        <li>Overige bepalingen</li>
                      </ol>
                    </div>
                    <div>
                      <ul>
                        <li>1</li>
                        <li>2</li>
                        <li>3</li>
                        <li>3</li>
                        <li>4</li>
                        <li>5</li>
                        <li>6</li>
                        <li>8</li>
                        <li>8</li>
                        <li>9</li>
                        <li>9</li>
                        <li>9</li>
                        <li>10</li>
                        <li>11</li>
                        <li>11</li>
                        <li>12</li>
                        <li>12</li>
                        <li>13</li>
                        <li>13</li>
                        <li>13</li>
                        <li>14</li>
                        <li>14</li>
                        <li>14</li>
                      </ul>
                    </div>
                  </div>
                </div>*/}
                <div className="page-section">
                  <h3>ALGEMEEN</h3>
                  <div className={ TermsStyles['page-nested-list'] }>
                    <ol className={ `${ TermsStyles['sub-list'] } ${ TermsStyles.main }` }>
                      <li><span>Definities</span>
                        <ol className={ TermsStyles['sub-list'] }>
                          <li>
                            In deze Algemene Voorwaarden hebben de volgende
                            woorden en uitdrukkingen de volgende betekenis, tenzij de context anders vereist:
                            <div className={ TermsStyles['page-table'] }>
                              <div><strong>Algemene Voorwaarden</strong></div>
                              <div>
                                deze algemene voorwaarden;
                              </div>
                              <div><strong>Artikel</strong></div>
                              <div>
                                een artikel in deze Algemene Voorwaarden;
                              </div>
                              <div><strong>Beleid</strong></div>
                              <div>
                                het van toepassing zijnde beleid van YourDesq,
                                al dan niet uiteengezet in een aanvullend document;
                              </div>
                              <div><strong>Betalingsvoorwaarden</strong></div>
                              <div>
                                voorwaarden waarin betaaldiensten worden geregeld die
                                worden geleverd aan Leden door de YourDesq betalingsentiteiten;
                              </div>
                              <div><strong>Boeking</strong></div>
                              <div>
                                een contract gesloten tussen Gebruiker en Verhuurder
                                op het moment dat Verhuurder de Reservering van Gebruiker accepteert;
                              </div>
                              <div><strong>Boekingswijzigingen</strong></div>
                              <div>
                                alle boekingswijzigingen die Gebruikers en Hosts aanbrengen
                                via het YourDesq Platform of die de klantenservice van YourDesq namens hen uitvoert;
                              </div>
                              <div><strong>Content</strong></div>
                              <div>
                                Door Gebruikers en Hosts aangeleverde content in welke vorm en
                                via welk middel dan ook, waaronder maar niet beperkt tot
                                Feedback, tekst, foto&apos;s, audio, video, informatie;
                              </div>
                              <div><strong>Diensten van derden</strong></div>
                              <div>
                                websites, applicaties, diensten of bronnen van derden;
                              </div>
                              <div><strong>Feedback</strong></div>
                              <div>
                                feedback, opmerkingen en suggesties voor verbeteringen van de Verhuurdiensten;
                              </div>
                              <div><strong>Gebruikers</strong></div>
                              <div>
                                Leden die Werklocaties en Verhuurdiensten zoeken, boeken of gebruiken;
                              </div>
                              <div><strong>Leden</strong></div>
                              <div>
                                alle Gebruikers en Hosts;
                              </div>
                              <div><strong>No-show</strong></div>
                              <div>
                                is sprake van indien de Gebruiker niet op locatie aanwezig is voor einde check-in tijd en derhalve geen gebruik maakt van de geboekte Werklocate en/of Verhuurdienst;
                              </div>
                              <div><strong>Reservering</strong></div>
                              <div>
                                een optie tot het boeken van een Werklocatie;
                              </div>
                              <div><strong>Schadeclaim</strong></div>
                              <div>
                                Als een Lid aantoont dat een ander Lid zijn of haar
                                bezittingen op verwijtbare wijze heeft beschadigd;
                              </div>
                              <div><strong>Totale Prijs</strong></div>
                              <div>
                                alle kosten die voor rekening komen van een Gebruiker
                                na het doen van een Boeking, inclusief de YourDesq Advertentie
                                prijs, van toepassing zijnde kosten zoals offline kosten,
                                belastingen en alle andere items die worden aangegeven tijdens het afrekenen;
                              </div>
                              <div><strong>Type Werkruimte</strong></div>
                              <div>
                                een of meerdere werkruimtes binnen een Werklocatie
                                waaronder maar niet beperkt tot een openbare plek,
                                een privé kantoor, privé bureau, kantoorruimte, samenwerkingsruimte
                                en evenementenruimte;
                              </div>
                              <div><strong>Hosts</strong></div>
                              <div>
                                Leden die Verhuurdiensten publiceren en aanbieden;
                              </div>
                              <div><strong>Verhuurdiensten</strong></div>
                              <div>
                                een verscheidenheid aan andere diensten die naast Werklocaties door Hosts worden aangeboden;
                              </div>
                              <div><strong>Website</strong></div>
                              <div>
                                www.yourdesq.com of enige andere website via
                                welke wij de Verhuurdiensten van tijd tot tijd ter
                                beschikking stellen;
                              </div>
                              <div><strong>Werklocaties</strong></div>
                              <div>
                                Werklocaties die Hosts aanbieden, welke kunnen worden gecategoriseerd
                                als hotels, coworking operators, restaurants, kantoorverdiepingen en kantoorgebouwen;
                              </div>
                              <div><strong>YourDesq Account</strong></div>
                              <div>
                                het account dat Gebruikers en Hosts moeten registreren om
                                toegang te krijgen tot en gebruik te kunnen maken van de functies van het YourDesq Platform;
                              </div>
                              <div><strong>YourDesq Advertentie</strong></div>
                              <div>
                                elke Verhuurdienst die wordt aangeboden;
                              </div>
                              <div><strong>YourDesq Betalingen</strong></div>
                              <div>
                                alle YourDesq entiteiten die betalingen incasseren en verrichten;
                              </div>
                              <div>
                                <strong>YourDesq Platform</strong>
                              </div>
                              <div>
                                de websites, applicaties en andere aanbiedingen van YourDesq;
                              </div>
                            </div>
                          </li>
                          <li>
                            Een persoon omvat een natuurlijke persoon, een rechtspersoon
                            of een lichaam zonder rechtspersoonlijkheid (met of zonder afzonderlijke
                            rechtspersoonlijkheid) en de wettelijke en persoonlijke vertegenwoordigers,
                            opvolgers en toegestane rechtverkrijgenden van die persoon.
                          </li>
                          <li>
                            Een verwijzing naar een vennootschap omvat alle vennootschappen, verenigingen
                            of andere rechtspersonen, waar en hoe ook opgericht of gevestigd.
                          </li>
                          <li>
                            Een verwijzing naar een wet, wettelijke bepaling of ondergeschikte wetgeving
                            is een verwijzing naar de wet, wettelijke bepaling of ondergeschikte wetgeving
                            zoals die van tijd tot tijd van kracht is, rekening houdend met een wijziging of het
                            opnieuw van kracht worden ervan, en omvat elke wet, wettelijke bepaling of andere soort
                            wetgeving die door de wet, wettelijke bepaling of ondergeschikte wetgeving wordt gewijzigd
                            of opnieuw van kracht wordt.
                          </li>
                          <li>
                            Een verwijzing naar een document is een verwijzing naar dat
                            document zoals het op enig moment wordt gewijzigd of vervangen (in elk geval,
                            anders dan in strijd met deze Voorwaarden).
                          </li>
                          <li>
                            Elke uitdrukking die wordt voorafgegaan door de woorden &quot;met inbegrip van&quot;,
                            &quot;omvatten&quot;, &quot;in het bijzonder&quot; of soortgelijke uitdrukkingen, heeft een illustratieve
                            waarde en beperkt niet de betekenis van de woorden die aan deze woorden voorafgaan.
                          </li>
                          <li>Woorden in het enkelvoud omvatten ook het meervoud en vice versa.</li>
                          <li>
                            Elke verplichting die krachtens deze Voorwaarden op een persoon
                            rust om iets niet te doen, omvat ook de verplichting om niet in te stemmen
                            met of toe te staan dat een dergelijke handeling wordt verricht.
                          </li>
                        </ol>
                      </li>
                      <li>
                        <span className={ TermsStyles.anchor } id="toepasselijkheid">Toepasselijkheid van de Algemene Voorwaarden</span>
                        <ol className={ TermsStyles['sub-list'] }>
                          <li>
                            Voor (toegang tot of gebruik van) bepaalde aspecten van de
                            Verhuurdiensten kunnen afwijkende voorwaarden zijn geplaatst
                            of kan het nodig zijn dat u instemt met en akkoord gaat met aanvullende
                            voorwaarden. Indien er een conflict is tussen deze Algemene Voorwaarden en
                            de voorwaarden die zijn geplaatst voor een specifiek gebied en/of onderdeel
                            van de Verhuurdiensten, hebben de laatstgenoemde voorwaarden voorrang met
                            betrekking tot uw gebruik van of toegang tot dat gebied van de Verhuurdiensten.
                          </li>
                          <li>
                            U erkent en gaat ermee akkoord dat u door het publiceren
                            en aanbieden van of het zoeken, boeken en gebruiken van
                            de Verhuurdiensten op het YourDesq Platform, dat u deze
                            Algemene Voorwaarden hebt gelezen en dat u deze begrijpt en
                            ermee instemt eraan gebonden te zijn, ongeacht of u zich al dan
                            niet bij ons hebt geregistreerd. Indien u niet akkoord gaat met
                            deze Algemene Voorwaarden, dan hebt u geen recht op toegang tot of
                            gebruik van de Verhuurdiensten op het YourDesq Platform.
                          </li>
                          <li>
                            Indien u deze Algemene Voorwaarden aanvaardt of ermee instemt
                            namens een bedrijf of een andere rechtspersoon, verklaart en
                            garandeert u dat u de bevoegdheid hebt om dat bedrijf of die
                            andere rechtspersoon aan deze Algemene Voorwaarden te binden.
                            In dat geval verwijst &quot;u&quot; en &quot;uw&quot; naar en zijn de Algemene Voorwaarden
                            op dat bedrijf of die andere rechtspersoon van toepassing.
                          </li>
                          <li>
                            Indien één of meer bepalingen van deze Voorwaarden ongeldig
                            zijn of vernietigd worden, blijven de overige bepalingen onverkort
                            van toepassing. YourDesq zal zich naar beste kunnen inspannen om
                            de ongeldige bepalingen te vervangen door nieuwe bepalingen, die wat
                            betreft doel en strekking zo nauw mogelijk aansluiten bij de oude bepalingen.
                          </li>
                        </ol>
                      </li>
                      <li>
                        <span className={ TermsStyles.anchor } id="using_yourdesq">Gebruik van het YourDesq Platform</span>
                        <ol className={ TermsStyles['sub-list'] }>
                          <li>
                            Gebruiker en Verhuurder mogen onder geen voorwaarde handelen
                            in strijd met het Non-discriminatiebeleid en Privacybeleid. Ingeval
                            er iets mis is en/of er niet wordt voldaan aan wat er beloofd is, kan er
                          contact worden opgenomen met <a href="mailto:hello@yourdesq.com">hello@yourdesq.com</a>.
                          </li>
                          <u className={ TermsStyles.anchor } id="users_1">Gebruikers</u>
                          <li>
                            U moet een YourDesq Account registreren om toegang te krijgen tot en gebruik
                            te maken van veel functies van het YourDesq Platform. Registratie is alleen
                            toegestaan voor rechtspersonen, partnerschappen en natuurlijke personen die
                            18 jaar of ouder zijn. U verklaart en garandeert dat u geen persoon of entiteit
                            bent die het YourDesq Platform niet mag gebruiken volgens de Nederlandse wet,
                            de plaats waar u woont, of enig ander toepasselijk rechtsgebied. U moet juiste,
                            actuele en volledige informatie verstrekken tijdens de registratie en uw accountgegevens
                            up-to-date houden. U mag niet meer dan één YourDesq Account registreren of uw
                            YourDesq Account overdragen aan iemand anders. U bent verantwoordelijk voor het
                            handhaven van de vertrouwelijkheid en veiligheid van uw accountgegevens en mag uw
                            gegevens niet bekendmaken aan derden. U moet YourDesq onmiddellijk op de hoogte
                            stellen als u vermoedt dat uw inloggegevens verloren zijn gegaan, gestolen zijn of
                            dat uw YourDesq Account op een andere manier in gevaar is gebracht. U bent verantwoordelijk
                            en aansprakelijk voor activiteiten die worden uitgevoerd via uw YourDesq Account, tenzij
                            deze activiteiten niet door u zijn geautoriseerd en u niet anderszins nalatig bent
                            geweest (zoals het niet melden van ongeautoriseerd gebruik of verlies van je inloggegevens).
                            Indien en voor zover toegestaan door de toepasselijke wetgeving, kunnen we, maar zijn
                            we niet verplicht om (i) u te vragen om identificatie of andere informatie, (ii) controles
                            uit te voeren om uw identiteit of achtergrond te verifiëren, (iii) u te screenen
                            met databases van derden of andere bronnen en rapporten op te vragen bij dienstverleners,
                            en (iv) rapporten te verkrijgen uit openbare registers van strafrechtelijke
                            veroordelingen of registraties van zedendelinquenten of hun lokale equivalenten.
                          </li>
                          <li>
                            U kunt zoeken naar Verhuurdiensten door gebruik te maken van criteria zoals
                            het type Verhuurdiensten, Werklocatie categorie, Type Werkruimte, boekingsdata, wat voor
                            voorzieningen u nodig heeft en het aantal Gebruikers. Zoekresultaten zijn gebaseerd op hun
                            relevantie voor uw zoekopdracht. Relevantie houdt rekening met factoren zoals prijs,
                            beschikbaarheid, reviews, klantenservice en annuleringsgeschiedenis, populariteit, eerdere
                            reizen en opgeslagen YourDesq Advertenties, vereisten van de
                            Verhuurder (bv. beroep, branche), en meer.
                          </li>
                          <li>
                            Een Reservering voor een Verhuurdienst geeft u een optie op het tot stand komen
                            van een Boeking. Wanneer de Verhuurder uw Reservering accepteert, komt er een Boeking
                            tot stand. Een Boeking voor:
                            <ol className={ TermsStyles['alphabetic-list'] }>
                              <li>
                                een Werklocatie geeft u een beperkte licentie om de Werklocatie te betreden,
                                te bezetten en te gebruiken. De Verhuurder behoudt het recht om de Werklocatie
                                opnieuw te betreden tijdens uw verblijf in de Werklocatie, in de mate dat: (i)
                                het redelijkerwijs noodzakelijk is, (ii) het is toegestaan door uw contract met
                                de Verhuurder, en (iii) in overeenstemming is met de toepasselijke wetgeving. Als u
                                na het uitchecken blijft, heeft de Verhuurder het recht u te laten vertrekken op een
                                wijze die in overeenstemming is met de toepasselijke wetgeving, inclusief het opleggen
                                van redelijke boetes voor overschrijding van de toegestane verblijfsduur. U mag het
                                maximum aantal toegestane Gebruikers niet overschrijden.
                              </li>
                              <li>
                                een Verhuurdienst geeft u het recht om deel te nemen aan, aanwezig te zijn
                                bij, of gebruik te maken van die Verhuurdienst. U bent verantwoordelijk
                                voor de bevestiging dat u, en iedereen die u uitnodigt, voldoet aan de
                                minimumleeftijd, branche en beroep of andere vereisten. U bent verantwoordelijk
                                voor het informeren van de Verhuurder over eventuele medische of lichamelijke
                                aandoeningen of andere omstandigheden die van invloed kunnen zijn op uw
                                vermogen om deel te nemen, de Verhuurdienst bij te wonen of er gebruik van
                                te maken. Behalve indien uitdrukkelijk toegestaan, mag u geen personen toelaten
                                tot een Verhuurdienst tenzij zij als extra Gebruiker zijn opgenomen tijdens
                                het boekingsproces.
                              </li>
                            </ol>
                          </li>
                          <li>
                            Indien er sprake is van een No-show, wordt de betaling binnen 24 uur na
                            de oorspronkelijke check-in, conform het bepaalde in Artikel 5, vrijgegeven
                            aan YourDesq en de Verhuurder.
                          </li>
                          <u className={ TermsStyles.anchor } id="hosts_1">Hosts</u>
                          <li>
                            Het is onze missie om de mensen en het kantoor weer productief te maken.
                            Wij bieden de Verhuurder de mogelijkheid om hun Werklocatie of andere
                            Verhuurdienst te delen met onze levendige gemeenschap van Gebruikers.
                            Het is makkelijk en gratis om een YourDesq Advertentie aan te maken en u
                            hebt de controle over hoe u verhuurt - bepaal uw prijs, beschikbaarheid en
                            regels voor elke YourDesq Advertentie
                          </li>
                          <li>
                            Wanneer u een Reservering accepteert via het YourDesq Platform, gaat u
                            rechtstreeks met de Gebruiker een contract aan, en bent u verantwoordelijk
                            voor het leveren van de Verhuurdienst onder de voorwaarden en tegen de prijs
                            zoals gespecificeerd in uw YourDesq Advertentie.
                          </li>
                          <li>
                            Uw relatie met YourDesq is die van een onafhankelijk individu of entiteit
                            en niet die van een werknemer, agent, joint venturer of partner van YourDesq.
                            YourDesq Betalingen treedt slechts op als een incassobureau zoals beschreven
                            in de Betalingsvoorwaarden. YourDesq bepaalt of controleert uw Verhuurdiensten
                            niet, en u begrijpt dat u volledig zelf kunt bepalen of en wanneer, tegen
                            welke prijs en onder welke voorwaarden u Verhuurdiensten aanbiedt.
                          </li>
                          <li>
                            Gebruikers en Hosts zijn verantwoordelijk voor alle Boekingswijzigingen die
                            zij aanbrengen via het YourDesq Platform of die de klantenservice van
                            YourDesq namens hen uitvoert, en gaan akkoord met het betalen van extra
                            bedragen, kosten of belastingen in verband met een Boekingswijziging.
                          </li>
                          <li>
                            U heeft altijd het recht de Gebruiker te verzoeken zich te legitimeren.
                            Indien getoonde legitimatiebewijs niet overeenstemt met door Gebruiker op
                            het YourDesq Account geregistreerde legitimatiebewijs, heeft Verhuurder altijd
                            het recht Gebruiker (i) de toegang tot de Werklocatie en/of Verhuurdienst te weigeren;
                            (ii) Gebruiker op te dragen de Werklocatie en/of Verhuurdienst te verlaten. In een dergelijk
                            geval heeft Gebruiker géén recht op restitutie.
                          </li>
                          <li>
                            Onderhoud van uw YourDesq Advertentie
                          </li>
                        </ol>
                      </li>
                      <li>
                        <span className={ TermsStyles.anchor } id="advertisement_maintenance">Onderhoud van uw YourDesq Advertentie</span>
                        <ol className={ TermsStyles['sub-list'] }>
                          <li>
                            Het YourDesq Platform biedt tools waarmee u eenvoudig een YourDesq Advertentie kunt
                            opzetten en beheren. Uw YourDesq Advertentie moet volledige en accurate informatie bevatten
                            over uw Verhuurdiensten, uw prijs, andere kosten zoals schoonmaakkosten, resort kosten,
                            borgsommen, offline kosten, en eventuele regels of eisen die van toepassing zijn op uw
                            Gebruikers of YourDesq Advertentie. U bent verantwoordelijk voor uw handelen en nalaten
                            en voor het te allen tijde up-to-date en accuraat houden van je YourDesq Advertentie informatie
                            (inclusief kalender beschikbaarheid) en inhoud (zoals foto&lsquo;s). Wij raden u aan om een passende
                            verzekering af te sluiten voor uw Verhuurdiensten en raden u aan om de polisvoorwaarden,
                            zoals dekkingsdetails en uitsluitingen, zorgvuldig door te nemen. U mag slechts één type
                            Werklocatie per YourDesq Advertentie onderhouden, maar u mag meerdere YourDesq Advertenties
                            hebben voor één Werklocatie als deze meerdere Werkruimtes heeft om aan te bieden aan Gebruikers.
                          </li>
                          <li>
                            U bent verantwoordelijk voor het begrijpen en naleven van alle wetten,
                            regels, voorschriften en contracten met derden die van toepassing zijn op
                            uw YourDesq Advertentie of Verhuurdiensten. Bijvoorbeeld: Sommige Hosts en huurcontracten,
                            of regels van kantooreigenaren en condominiumverenigingen, beperken of verbieden onderverhuur,
                            korte termijnverhuur en/of langer verblijf in uw Werklocatie. Sommige steden hebben
                            bestemmingsplannen of andere wetten die de korte termijnverhuur van kantoorpanden beperken.
                            In sommige jurisdicties moeten Hosts zich registreren, een vergunning aanvragen of een licentie
                            verkrijgen voordat zij bepaalde Verhuurdiensten mogen verlenen (zoals verhuur op korte termijn,
                            terugkerende Boekingen, het bereiden van eten en drinken).
                          </li>
                          <li>
                            Op sommige plaatsen kunnen de Verhuurdiensten die u wilt aanbieden helemaal
                            verboden zijn. Sommige jurisdicties vereisen dat u Gebruikers die uw Werklocatie
                            gebruiken registreert. Sommige jurisdicties hebben wetten die huurrechten voor Gebruikers
                            en extra verplichtingen voor Hosts creëren. Bijvoorbeeld, sommige plaatsen hebben
                            huisbaas-huurder, huur controle, en uitzettingswetten die van toepassing kunnen zijn op
                            Gebruikers die boeken voor terugkerende verblijven in uw Werklocatie. Controleer uw plaatselijke
                            regels om te weten welke regels van toepassing zijn op de Verhuurdiensten die u van plan bent aan
                            te bieden. Informatie die wij verstrekken met betrekking tot wettelijke vereisten is alleen voor
                            informatieve doeleinden en u dient onafhankelijk uw verplichtingen te bevestigen. U
                            bent verantwoordelijk voor het behandelen en gebruiken van persoonlijke gegevens van Gebruikers
                            en anderen in overeenstemming met de toepasselijke privacywetgeving en deze Algemene Voorwaarden,
                            met inbegrip van ons Privacybeleid. Indien u vragen heeft over hoe lokale wetten van toepassing
                            zijn, dient u altijd juridisch advies in te winnen.
                          </li>
                          <li>
                            De rangschikking van YourDesq Advertenties in de zoekresultaten op het YourDesq
                            Platform hangt af van een aantal factoren, waaronder voornamelijk de volgende parameters:
                            <ol className={ TermsStyles['alphabetic-list'] }>
                              <li>
                                zoekparameters van de Gebruiker (bijv. aantal gebruikers, duur van de Boeking, prijsklasse);
                              </li>
                              <li>
                                YourDesq Advertentie kenmerken (bijv. prijs, beschikbaarheid van de kalender,
                                aantal en kwaliteit van de afbeeldingen, Reviews, type Verhuurdienst, Verhuurder status,
                                leeftijd van de YourDesq Advertentie, gemiddelde populariteit van de Gebruiker);
                              </li>
                              <li>
                                Ervaring van de Gebruiker met het boeken (bijv. klantenservice en annuleringsgeschiedenis
                                van de Verhuurder, gemak van boeken);
                              </li>
                              <li>Hosts eisen (bijv. minimum of maximum dagen, Boeking cut-off tijd); en</li>
                              <li>
                                Gebruikers voorkeuren (bijv. eerdere Boekingen, opgeslagen YourDesq Advertenties,
                                locatie van waaruit de Gebruiker zoekt).
                              </li>
                            </ol>
                            Zoekresultaten kunnen anders verschijnen op onze mobiele webapplicatie
                            dan op onze desktop Website. YourDesq kan Hosts toestaan om hun YourDesq
                            Advertenties te promoten in zoekopdrachten of elders op het YourDesq Platform tegen
                            betaling van een extra vergoeding.
                          </li>
                          <li>
                            U bent verantwoordelijk voor uw eigen handelen en nalaten en bent ook verantwoordelijk voor
                            het handelen en nalaten van iedereen die u toestaat deel te nemen aan het leveren van uw
                            Verhuurdiensten. U bent zelf verantwoordelijk voor het bepalen van uw prijs en het opstellen van
                            regels en eisen voor uw YourDesq Advertentie. YourDesq kan advies geven over de prijs die de
                            Verhuurder bepaalt voor een YourDesq Advertentie, maar YourDesq is op geen enkele manier
                            verantwoordelijk voor het effect dat de prijs heeft op het aantal Boekingen via de YourDesq
                            Advertentie. U moet alle kosten en vergoedingen beschrijven in uw YourDesq Advertentie beschrijving
                            en u mag geen extra kosten of vergoedingen innen buiten het YourDesq Platform om, behalve de
                            kosten en vergoedingen die uitdrukkelijk zijn toegestaan door ons Offline Fee Policy. U mag
                            Gebruikers niet aanmoedigen om accounts bij derden aan te maken, beoordelingen in te dienen,
                            hun contactgegevens te verstrekken of andere acties te ondernemen buiten het YourDesq Platform
                            in strijd met ons Off-Platform Beleid.
                          </li>
                          <li>
                            Als u samenwerkt met een mede-Verhuurder of Verhuurder als onderdeel van een team, bedrijf
                            of andere organisatie, is de entiteit en elk individu dat deelneemt aan het leveren van
                            Verhuurdiensten, verantwoordelijk en aansprakelijk als een Verhuurder onder deze Algemene
                            Voorwaarden. Indien u voorwaarden accepteert of contracten aangaat, verklaart en garandeert
                            u dat u bevoegd bent om contracten aan te gaan namens, en uw team, bedrijf of andere organisatie
                            te binden, en dat elke entiteit die u gebruikt van goede reputatie is onder de wetten van
                            de plaats waar het gevestigd is. Als u andere functies uitvoert, verklaart en garandeert u
                            dat u bevoegd bent om die functies uit te voeren.
                          </li>
                        </ol>
                      </li>
                      <li>
                        <span className={ TermsStyles.anchor } id="payment_terms">Betalingsvoorwaarden</span>
                        <ol className={ TermsStyles['sub-list'] }>
                          <li>
                            Wij bieden een platform dat Leden in staat stelt Verhuurdiensten te publiceren,
                            aan te bieden, te zoeken en te boeken. Indien een Gebruiker een Boeking doet, wordt
                            het volledige bedrag van de betaling gestort op een rekening die YourDesq aanhoudt
                            bij Stripe vanuit waar het bedrag wordt gesplitst volgens een vaste <strong>“Commissie”</strong>: van
                            het bedrag van de Reservering wordt 20% overgemaakt aan YourDesq, 80% wordt op de
                            rekening van de Verhuurder gestort. De deelbedragen worden vrijgegeven binnen enkele dagen nadat de boeking heeft plaats gevonden
                          </li>
                          <li>
                            YourDesq is geen partij bij of deelnemer van een Reservering, en zal dat ook niet worden.
                            YourDesq treedt niet op als vertegenwoordiger voor een Lid, behalve wanneer YourDesq
                            Betalingen optreedt als incassomedewerker zoals bepaald in de Betalingsvoorwaarden. Voor
                            alle overige informatie wordt u verwezen naar de Betalingsvoorwaarden.
                          </li>
                          <u className={ TermsStyles.anchor } id="users_2">Gebruikers</u>
                          <li>
                            Wanneer u een YourDesq Advertentie boekt, gaat u akkoord met het betalen van alle kosten
                            voor uw Boeking, inclusief de YourDesq Advertentie prijs, van toepassing zijnde kosten zoals
                            offline kosten, belastingen en alle andere items die worden aangegeven tijdens het afrekenen
                            (gezamenlijk, <strong>&quot;Totale Prijs</strong>&quot;). U stemt er ook mee in dat YourDesq Betalingen een borg in rekening
                            mag brengen en innen. Wanneer u de boekingsbevestiging ontvangt, is er sprake een Reservering.
                            De annuleringsvoorwaarden en eventuele andere regels, normen, beleidsregels of vereisten die in
                            de YourDesq Advertentie of tijdens het afrekenen worden vermeld, maken deel uit van uw Reservering.
                          </li>
                          <u className={ TermsStyles.anchor } id="hosts_2">Hosts</u>
                          <li>
                            U stemt ermee in om de van toepassing zijnde kosten te betalen, zoals Commissie
                            (en van toepassing zijnde belastingen) voor elke Boeking. YourDesq Betalingen houdt de door
                            u verschuldigde bedragen in op uw uitbetaling, tenzij tussen YourDesq en u een andere
                            methode is overeengekomen. Alle voorwaarden of bepalingen die u opneemt in een aanvullend
                            contract met Gebruikers moeten: (i) in overeenstemming zijn met deze Algemene Voorwaarden,
                            ons Beleid, en de informatie in uw YourDesq Advertentie, en (ii) duidelijk worden vermeld in
                            uw YourDesq Advertentie beschrijving.
                          </li>
                          <li>
                            Als u YourDesq opdracht geeft een deel van uw uitbetaling over te maken aan
                            een mede-Verhuurder of een andere Verhuurder, of betalingen naar iemand anders
                            te sturen, moet u hiertoe gemachtigd zijn, en bent u verantwoordelijk en aansprakelijk
                            voor de uitbetalingsbedragen en de juistheid van alle uitbetalingsinformatie die u verstrekt.
                          </li>
                        </ol>
                      </li>
                      <li>
                        <span className={ TermsStyles.anchor } id="committee">Commissie</span>
                        <ol className={ TermsStyles['sub-list'] }>
                          <li>
                            YourDesq kan kosten (en toepasselijke belastingen) in rekening brengen aan Gebruikers
                            en Hosts voor het gebruik van het YourDesq Platform (de <strong>“Commissie”</strong>). Behalve indien anders
                            vermeld op het YourDesq Platform, wordt de Commissie niet terugbetaald. YourDesq behoudt zich
                            het recht voor om de Commissie op elk gewenst moment te wijzigen, en zal Leden op de hoogte
                            stellen van eventuele wijzigingen voordat deze van kracht worden. Wijzigingen in de Commissie
                            hebben geen invloed op Boekingen die zijn gemaakt voorafgaand aan de ingangsdatum van de wijziging.
                            Als u het niet eens bent met een tariefswijziging kunt u uw overeenkomst op elk gewenst moment
                            beëindigen op grond van Artikel 14.
                          </li>
                        </ol>
                      </li>
                      <li>
                        <span className={ TermsStyles.anchor } id="users_behavior">Gedrag van Gebruikers</span>
                        <ol className={ TermsStyles['sub-list'] }>
                          <li>
                            Als u (i) in strijd handelt met deze Algemene Voorwaarden, ons Beleid, of onze Standaarden,
                            (ii) in strijd handelt met toepasselijke wet- en regelgeving of rechten van derden, (iii)
                            herhaaldelijk slechte beoordelingen hebt ontvangen of YourDesq anderszins op de hoogte raakt
                            van of klachten heeft ontvangen over uw prestaties of gedrag, (vi) herhaaldelijk bevestigde
                            Boekingen hebt geannuleerd of niet reageert op boekingsverzoeken zonder geldige reden, of (vii)
                            een dergelijke actie noodzakelijk is om de persoonlijke veiligheid of eigendommen van YourDesq,
                            haar Leden, of derden te beschermen, kan YourDesq:
                            <ol className={ TermsStyles['alphabetic-list'] }>
                              <li>
                                uw toegang tot of gebruik van het YourDesq Platform en/of uw YourDesq
                                Account opschorten of beperken;
                              </li>
                              <li>
                                YourDesq Advertenties, reacties of andere Content opschorten of verwijderen;
                              </li>
                              <li>
                                in behandeling zijnde of bevestigde Boekingen annuleren; of
                              </li>
                              <li>
                                een speciale status verbonden aan uw YourDesq Account opschorten of intrekken.
                              </li>
                            </ol>
                          </li>
                          <li>
                            In geval van niet-materiële overtredingen of wanneer anderszins van toepassing,
                            wordt u op de hoogte gesteld van een voorgenomen maatregel door YourDesq en krijgt
                            u de gelegenheid om de kwestie op te lossen, tenzij een dergelijke kennisgeving (i)
                            de opsporing of preventie van fraude of andere illegale activiteiten zou voorkomen of
                            belemmeren, (ii) de legitieme belangen van andere Leden of derden zou schaden, of (iii)
                            in strijd zou zijn met toepasselijke wetgeving.
                          </li>
                        </ol>
                      </li>
                      <li>
                        <span className={ TermsStyles.anchor } id="privacy">Privacy</span>
                        <ol className={ TermsStyles['sub-list'] }>
                          <li>
                            Wij hanteren andere voorwaarden en beleidsregels die deze Algemene Voorwaarden aanvullen,
                            zoals ons Privacybeleid, welke onze verzameling en gebruik van persoonsgegevens beschrijft.
                            Als Verhuurder bent u verantwoordelijk voor het behandelen en gebruiken van persoonlijke gegevens
                            van Gebruikers en anderen in overeenstemming met de toepasselijke privacywetgeving en deze
                            Algemene Voorwaarden, met inbegrip van ons Privacybeleid. Als u vragen hebt over hoe de lokale
                            wetgeving van toepassing is, moet u altijd juridisch advies inwinnen.
                          </li>
                        </ol>
                      </li>
                      <li>
                        <span className={ TermsStyles.anchor } id="intellectual_property">Intellectueel Eigendom</span>
                        <ol className={ TermsStyles['sub-list'] }>
                          <li>
                            De Verhuurdiensten en de Content zijn beschermd door het auteursrecht, merkenrecht en andere
                            wetten van Nederland en andere rechtsgebieden. U erkent en gaat ermee akkoord dat de
                            Verhuurdiensten en de Content, met inbegrip van alle bijbehorende intellectuele eigendomsrechten,
                            het exclusieve eigendom zijn van YourDesq en haar licentiegevers. U zult geen mededelingen
                            over auteursrechten, handelsmerken, dienstmerken of andere eigendomsrechten die in de
                            Verhuurdiensten of de Content zijn opgenomen, verwijderen, wijzigen of onleesbaar maken.
                          </li>
                          <li>
                            Alle handelsmerken, dienstmerken, logo&apos;s, handelsnamen en alle andere eigendomsaanduidingen
                            van YourDesq gebruikt in/op het YourDesq Platform of de Verhuurdiensten zijn handelsmerken
                            of gedeponeerde handelsmerken van YourDesq. Alle andere handelsmerken, dienstmerken, logo&apos;s,
                            handelsnamen en alle andere eigendomsaanduidingen zijn de handelsmerken of geregistreerde
                            handelsmerken van hun respectievelijke eigenaars.
                          </li>
                          <li>
                            YourDesq respecteert het auteursrecht en verwacht dat u hetzelfde doet. Het is YourDesq’s
                            beleid om, indien nodig geacht door YourDesq, YourDesq Accounts te beëindigen van Leden
                            die inbreuk plegen of verondersteld worden inbreuk te plegen op de rechten van
                            auteursrechthouders.
                          </li>
                        </ol>
                      </li>
                      <li>
                        <span className={ TermsStyles.anchor } id="content">Content</span>
                        <ol className={ TermsStyles['sub-list'] }>
                          <li>
                            Delen van het YourDesq Platform stellen je in staat om Feedback, tekst, foto&lsquo;s, audio,
                            video, informatie en andere content (<strong>&quot;Content&quot;</strong>) te leveren. Door het verstrekken van
                            Content, in welke vorm en via welk middel dan ook, geef je YourDesq een niet-exclusieve,
                            wereldwijde, royalty-vrije en overdraagbare licentie, voor de duur van de bescherming van
                            de aldus gelicentieerde rechten, om toegang te krijgen, te gebruiken, op te slaan, te kopiëren,
                            aan te passen, te distribueren, te publiceren, uit te zenden, te streamen, uit te zenden en
                            anderszins op enigerlei wijze te exploiteren om het YourDesq Platform aan te bieden en/of te
                            promoten, in elke media of platform, bekend of onbekend tot op heden en in het bijzonder op
                            internet en sociale netwerken.
                          </li>
                          <li>
                            Indien de Content persoonlijke informatie bevat, wordt deze Content alleen voor deze
                            doeleinden gebruikt indien dit gebruik in overeenstemming is met de toepasselijke wetgeving
                            inzake gegevensbescherming, in overeenstemming met ons Privacybeleid. Indien YourDesq betaalt
                            voor de aanmaak van Content of de aanmaak van Content faciliteert, kan YourDesq eigenaar
                            zijn van die inhoud, in welk geval aanvullende voorwaarden of openbaarmakingen dat vermelden.
                            U bent zelf verantwoordelijk voor alle Content die u aanlevert en garandeert dat u de eigenaar
                            bent of bevoegd bent om YourDesq de rechten te verlenen zoals beschreven in deze voorwaarden.
                            U bent verantwoordelijk en aansprakelijk indien uw Content inbreuk maakt op of in strijd
                            is met de intellectuele eigendomsrechten of privacyrechten van derden. Content moet voldoen
                            aan ons Contentbeleid en ons Non-discriminatiebeleid, die onder andere discriminerende,
                            obscene, intimiderende, misleidende, gewelddadige en illegale Content verbieden. U stemt
                            ermee in dat YourDesq diensten of geautomatiseerde hulpmiddelen beschikbaar kan stellen
                            om Content te vertalen, en dat uw Content kan worden vertaald met behulp van dergelijke
                            diensten of hulpmiddelen. YourDesq garandeert niet de juistheid of kwaliteit van
                            vertalingen en Leden zijn verantwoordelijk voor het bevestigen van de juistheid van
                            dergelijke vertalingen.
                          </li>
                          <li>
                            Content die beschikbaar wordt gesteld via het YourDesq Platform kan beschermd zijn door
                            auteursrecht, handelsmerk en/of andere Nederlandse wetgeving en andere landen. U erkent
                            dat alle intellectuele eigendomsrechten voor die Content het exclusieve eigendom zijn van
                            YourDesq en/of haar licentiegevers en gaat ermee akkoord dat u geen mededelingen over
                            auteursrechten, handelsmerken, dienstmerken of andere eigendomsrechten verwijdert, wijzigt
                            of onleesbaar maakt. U mag geen Content gebruiken, kopiëren, aanpassen, wijzigen, er
                            afgeleide werken van maken, distribueren, in licentie geven, verkopen, overdragen,
                            publiekelijk tonen, publiekelijk uitvoeren, overdragen, uitzenden of anderszins exploiteren
                            waartoe u toegang hebt via het YourDesq Platform, behalve voor zover u de wettelijke
                            eigenaar bent van die Content of zoals uitdrukkelijk is toegestaan in deze Algemene
                            Voorwaarden. Onder voorbehoud van naleving van deze Algemene Voorwaarden, verleent
                            YourDesq u een beperkte, niet-exclusieve, niet-sublicentieerbare, herroepbare,
                            niet-overdraagbare licentie om (i) de applicatie te downloaden en te gebruiken op
                            je persoonlijke apparaat(en); en (ii) toegang te verkrijgen tot de Content die
                            beschikbaar is gesteld op of via het YourDesq Platform en toegankelijk is voor u,
                            uitsluitend voor persoonlijk en niet-commercieel gebruik.
                          </li>
                        </ol>
                      </li>
                      <li>
                        <span className={ TermsStyles.anchor } id="responsibility">Aansprakelijkheid</span>
                        <ol className={ TermsStyles['sub-list'] }>
                          <li>
                            U erkent en gaat ermee akkoord dat het volledige risico dat voortvloeit uit of verband houdt
                            met uw toegang tot en gebruik van de Verhuurdiensten en de Content, uw Reservering, en elk
                            contact dat u hebt met andere gebruikers van de Verhuurdiensten, hetzij in persoon of online,
                            bij u blijft. Noch YourDesq, noch enige andere partij die betrokken is bij de creatie,
                            productie of levering van de Verhuurdiensten en/of de Content zal aansprakelijk zijn voor enige
                            directe of indirecte schade, met inbegrip van winstderving, verlies van gegevens of verlies
                            van goodwill, onderbreking van de dienstverlening, computerschade of systeemfalen of de kosten
                            van vervangende producten of diensten, of voor enige schade voor persoonlijk of lichamelijk
                            letsel of emotioneel leed voortvloeiend uit of in verband met deze Algemene Voorwaarden, uit
                            het gebruik van of de onmogelijkheid tot gebruik van de Verhuurdiensten en/of de Content, uit
                            communicaties, interacties of ontmoetingen met andere gebruikers van de Verhuurdiensten of
                            andere personen met wie u communiceert als resultaat van uw gebruik van de Verhuurdiensten,
                            of van uw Reservering, al dan niet gebaseerd op garantie, contract, onrechtmatige daad,
                            productaansprakelijkheid of enige andere wettelijke basis, en ongeacht of YourDesq al
                            dan niet op de hoogte werd gebracht van de mogelijkheid van dergelijke schade, zelfs
                            indien een beperkte remedie hierin uiteengezet, niet aan zijn essentiële doel blijkt te voldoen.
                          </li>
                          <li>
                            YourDesq is slechts aansprakelijk volgens de wettelijke bepalingen voor opzet en grove nalatigheid
                            door ons, onze wettelijke vertegenwoordigers, bestuurders, of andere plaatsvervangende agenten.
                            Hetzelfde geldt voor de aanvaarding van garanties of enige andere risicoaansprakelijkheid, of
                            in geval van verwijtbaar letsel aan leven, lichaam of gezondheid. Voor nalatige schending
                            van essentiële contractuele verplichtingen door ons, onze wettelijke vertegenwoordigers,
                            bestuurders, of andere plaatsvervangende agenten is de aansprakelijkheid van YourDesq
                            beperkt tot de typisch optredende voorzienbare schade. Essentiële contractuele verplichtingen
                            zijn verplichtingen van YourDesq in de juiste nakoming waarvan u regelmatig vertrouwt en
                            moet vertrouwen voor de juiste uitvoering van het contract. Elke aanvullende aansprakelijkheid
                            van YourDesq is uitgesloten.
                          </li>
                        </ol>
                      </li>
                      <li>
                        <span className={ TermsStyles.anchor } id="complaints">Klachten en schadeclaims</span>
                        <ol className={ TermsStyles['sub-list'] }>
                          <li>
                            Als een Lid aantoont dat een ander Lid zijn of haar bezittingen op verwijtbare wijze heeft
                            beschadigd, kan het klagende Lid schadevergoeding eisen via het Resolution Center
                            (<strong>&quot;Schadeclaim&quot;</strong>). Als het klagende Lid een Schadeclaim escaleert naar YourDesq, krijgt het
                            andere Lid de kans om te reageren. Als het Lid dat reageert akkoord gaat met betaling,
                            of YourDesq bepaalt op basis van eventuele van toepassing zijnde wettelijke regels over
                            de bewijslast dat het Lid verantwoordelijk is voor de schadeclaim, kan YourDesq via YourDesq
                            Betalingen alle bedragen die nodig zijn om de schadeclaim te dekken, innen van het Lid
                            dat reageert en/of van een borg.
                          </li>
                          <li>
                            YourDesq kan alle rechtsmiddelen tegen u aanwenden die beschikbaar zijn onder de toepasselijke
                            wetgeving. U stemt ermee in te goeder trouw mee te werken, alle informatie te verstrekken
                            die YourDesq vraagt, documenten uit te voeren en verdere redelijke actie te ondernemen, in
                            verband met schadeclaims, klachten van Leden, claims onder verzekeringspolissen, of andere
                            claims gerelateerd aan uw levering of gebruik van Verhuurdiensten. U kunt in beroep gaan tegen
                            een beslissing van YourDesq door contact op te nemen met onze klantenservice. Beslissingen
                            van YourDesq met betrekking tot een Schadeclaim hebben geen invloed op uw contractuele en
                            wettelijke rechten. Uw recht om naar de rechter te stappen blijft onaangetast.
                          </li>
                        </ol>
                      </li>
                      <li>
                        <span className={ TermsStyles.anchor } id="cancellation">Annulering</span>
                        <ol className={ TermsStyles['sub-list'] }>
                          <li>
                            In het algemeen geldt dat, indien u een Reservering annuleert, het bedrag dat u terugkrijgt wordt
                            bepaald door de in het algemeen geldende annuleringsvoorwaarden die op die Reservering van
                            toepassing zijn:
                            <ol className={ TermsStyles['alphabetic-list'] }>
                              <li>
                                indien u meer dan 24 uur voor aanvang uw Reservering annuleert, wordt het volledige bedrag
                                van uw Reservering aan u gerestitueerd;
                              </li>
                              <li>
                                indien u tussen 24 en 12 uur voor aanvang uw Reservering annuleert, wordt 50% van het bedrag
                                van uw Reservering aan u gerestitueerd;
                              </li>
                              <li>
                                indien u korter dan 12 uur voor aanvang van uw Reservering annuleert, heeft u geen recht op
                                restitutie van (een gedeelte van) het bedrag van uw Reservering.
                              </li>
                            </ol>
                          </li>
                          <li>
                            In bepaalde situaties hebben andere beleidsregels voorrang en bepalen deze welk bedrag aan u
                            wordt terugbetaald:
                            <ol className={ TermsStyles['alphabetic-list'] }>
                              <li>
                                indien iets buiten uw macht u dwingt om een Reservering te annuleren, kunt u recht hebben
                                op een gedeeltelijke of volledige terugbetaling onder ons Verzachtende Omstandigheden Beleid;
                              </li>
                              <li>
                                indien de Verhuurder annuleert of als u een reisprobleem ondervindt (zoals gedefinieerd
                                in ons Restitutiebeleid voor Gebruikers), hebt u mogelijk recht op hulp bij het omboeken
                                of een gedeeltelijke of volledige restitutie onder ons Restitutiebeleid voor Gebruikers.
                              </li>
                            </ol>
                          </li>
                          <li>
                            U kunt in beroep gaan tegen een beslissing van YourDesq door contact op te nemen met
                            onze klantenservice.
                          </li>
                          <u className={ TermsStyles.anchor } id="landlord">Verhuurder</u>
                          <li>
                            Als Verhuurder mag u:
                            <ol className={ TermsStyles['alphabetic-list'] }>
                              <li>
                                slechts meer dan 48 uur voor aanvang dan een Reservering annuleren, mits niet wordt
                                geannuleerd in strijd met het Non-discriminatiebeleid en Privacybeleid.
                              </li>
                              <li>
                                een Gebruiker niet annuleren zonder een geldige reden volgens ons Verzachtende
                                Omstandigheden Beleid of de toepasselijke wetgeving. Als u een Gebruiker zonder
                                een dergelijke geldige reden annuleert, kunnen wij annuleringskosten in rekening
                                brengen en kan dit andere gevolgen opleggen.
                              </li>
                            </ol>
                          </li>
                          <li>
                            In het algemeen geldt dat, indien een Gebruiker een Reservering annuleert, het bedrag dat aan u wordt betaald,
                            wordt bepaald door de annuleringsvoorwaarden die op die Reservering van toepassing zijn. Indien: (i) een
                            Gebruiker een reiskwestie ondervindt (zoals gedefinieerd in het Restitutiebeleid voor Gebruikers), (ii)
                            zich een verzachtende omstandigheid voordoet, of (iii) een Reservering wordt geannuleerd op grond van
                            Artikel 13.1 en 13.2 van deze Algemene Voorwaarden, wordt het bedrag dat u wordt betaald verminderd met
                            het bedrag dat we terugbetalen of anderszins verstrekken aan de Gebruiker, en met alle andere redelijke
                            kosten die we maken als gevolg van de annulering
                          </li>
                          <li>
                            Als een gebruiker een terugbetaling ontvangt nadat u al betaald bent, of het bedrag van de terugbetaling
                            en andere door YourDesq gemaakte kosten hoger is dan uw uitbetaling, kan YourDesq
                            (via YourDesq Betalingen) dat bedrag op u verhalen, door bijvoorbeeld de terugbetaling
                            te verrekenen met uw toekomstige uitbetalingen. U gaat ermee akkoord dat YourDesq&apos;s Gebruikers
                            Restitutie Beleid, Verzachtende Omstandigheden Beleid, en deze Voorwaarden prevaleren boven
                            het annuleringsbeleid dat u hebt ingesteld in situaties waarin ze de annulering van een
                            Reservering en/of de uitgifte van restituties aan gebruikers toestaan.
                          </li>
                          <li>
                            U kunt in beroep gaan tegen een beslissing van YourDesq door contact
                            op te nemen met onze klantenservice.
                          </li>
                        </ol>
                      </li>
                      <li>
                        <span className={ TermsStyles.anchor } id="termination">Beëindiging</span>
                        <ol className={ TermsStyles['sub-list'] }>
                          <li>
                            De overeenkomst tussen u en YourDesq die in deze Algemene Voorwaarden tot uitdrukking komt,
                            blijft van kracht totdat u of wij de overeenkomst beëindigen in overeenstemming met deze
                            Algemene Voorwaarden.
                          </li>
                          <li>
                            U kunt uw overeenkomst te allen tijde beëindigen door ons een e-mail te sturen of
                            door uw YourDesq Account te verwijderen. YourDesq kan uw overeenkomst om welke reden
                            dan ook beëindigen door u 30 dagen van tevoren op de hoogte te stellen via e-mail of
                            via een andere contactinformatie die u heeft opgegeven voor uw YourDesq Account.
                            YourDesq kan uw overeenkomst ook onmiddellijk en zonder voorafgaande kennisgeving
                            beëindigen en de toegang tot het YourDesq Platform stopzetten indien (i) u wezenlijk
                            inbreuk maakt op deze voorwaarden of ons beleid, (ii) u toepasselijke wetten overtreedt,
                            of (iii) een dergelijke actie noodzakelijk is om de persoonlijke veiligheid of eigendommen
                            van YourDesq, haar Leden, of derden te beschermen (bijvoorbeeld in het geval van
                            frauduleus gedrag van een Lid), of (iv) uw Account meer dan twee jaar inactief is geweest.
                          </li>
                          <li>
                            Als u een Verhuurder bent en uw YourDesq Account beëindigt, worden alle bevestigde Boekingen
                            automatisch geannuleerd en krijgen uw Gebruikers een volledige terugbetaling. Als u uw
                            YourDesq Account als Gebruiker beëindigt, worden alle bevestigde Boekingen automatisch
                            geannuleerd en eventuele restitutie zal afhangen van de voorwaarden van de YourDesq Advertentie&lsquo;s
                            annuleringsvoorwaarden. Wanneer uw overeenkomst is beëindigd, heeft u geen recht op een
                            herstel van uw YourDesq Account of een van uw Content. Als uw toegang tot of gebruik van het
                            YourDesq Platform is beperkt, of uw YourDesq Account is opgeschort, of uw overeenkomst is
                            beëindigd door ons, mag u geen nieuw YourDesq Account registreren, toegang krijgen tot of
                            gebruik maken van het YourDesq Platform via een YourDesq Account van een ander Lid.
                          </li>
                        </ol>
                      </li>
                      <li>
                        <span className={ TermsStyles.anchor } id="reviews">Reviews</span>
                        <ol className={ TermsStyles['sub-list'] }>
                          <li>
                            Na elke Verhuurdienst krijgen Gebruikers en Hosts de kans om elkaar te beoordelen (de “Review”).
                            Uw Review moet accuraat zijn en mag geen discriminerende, beledigende, lasterlijke of andere
                            taal bevatten die in strijd is met ons Contentbeleid of Reviewbeleid. Reviews worden door
                            YourDesq niet gecontroleerd op juistheid en kunnen onjuist of misleidend zijn.
                          </li>
                        </ol>
                      </li>
                      <li>
                        <span className={ TermsStyles.anchor } id="disclaimer ">Disclaimer</span>
                        <ol className={ TermsStyles['sub-list'] }>
                          <li>
                            Wij onderschrijven of garanderen niet het bestaan, gedrag, prestaties, veiligheid, kwaliteit,
                            legaliteit of geschiktheid van een Gebruiker, Verhuurder, Verhuurdienst, YourDesq Advertentie
                            of derde partij en wij garanderen niet dat verificatie, identiteit of achtergrondcontroles
                            uitgevoerd op Leden (indien van toepassing) wangedrag uit het verleden zal identificeren of
                            toekomstig wangedrag zal voorkomen. Elke verwijzing naar een Lid dat &quot;geverifieerd&quot; is (of
                            vergelijkbare taal) geeft alleen aan dat het Lid of YourDesq een relevant verificatie- of
                            identificatieproces heeft voltooid en niets anders.
                          </li>
                          <li>
                            Wij zijn niet verantwoordelijk voor storingen of onderbrekingen van het internet en
                            telecommunicatie-infrastructuur die buiten onze controle liggen en kunnen leiden tot
                            onderbrekingen in de beschikbaarheid van het YourDesq Platform. YourDesq kan, tijdelijk en
                            rekening houdend met de legitieme belangen van de leden (bv. door voorafgaande kennisgeving),
                            de beschikbaarheid van het YourDesq Platform of bepaalde functies ervan beperken, indien dit
                            noodzakelijk is met het oog op capaciteitsbeperkingen, de veiligheid of integriteit van onze
                            servers, of om onderhoudsmaatregelen uit te voeren die de goede of verbeterde werking van
                            het YourDesq Platform verzekeren.
                          </li>
                        </ol>
                      </li>
                      <li>
                        <span className={ TermsStyles.anchor } id="indemnification">Vrijwaring</span>
                        <ol className={ TermsStyles['sub-list'] }>
                          <li>
                            Voor zover toegestaan door de toepasselijke wetgeving, gaat u ermee akkoord YourDesq
                            (inclusief YourDesq Betalingen, andere gelieerde ondernemingen en hun personeel) te ontslaan,
                            verdedigen (naar keuze van YourDesq), schadeloos te stellen en te vrijwaren van en tegen alle
                            claims, aansprakelijkheden, schade, verliezen en onkosten, inclusief, zonder beperking, redelijke
                            juridische en boekhoudkundige kosten, die voortkomen uit of op enigerlei wijze verband houden
                            met: (i) uw schending van deze Algemene Voorwaarden (inclusief aanvullende of additionele
                            voorwaarden die van toepassing zijn op een product of functie) of ons Beleid of Normen, (ii)
                            uw oneigenlijk gebruik van het YourDesq Platform, (iii) uw interactie met een Lid, verblijf
                            in een Werklocatie of andere Verhuurdienst, inclusief maar niet beperkt tot enige
                            verwondingen, verliezen of schade (ongeacht of deze compenserend is, direct, incidenteel,
                            gevolgschade of anderszins) van welke aard dan ook voortvloeiend in verband met of als
                            gevolg van een dergelijke interactie, verblijf, deelname of gebruik, (iv) uw verzuim, of
                            ons verzuim op uw aanwijzing, om belastingen nauwkeurig te melden, te innen of af te dragen,
                            of (v) uw schending van enige wet, regelgeving of rechten van derden, zoals intellectuele
                            eigendomsrechten of privacyrechten.
                          </li>
                          <li>
                            De vrijwaringsverplichting geldt alleen indien en voor zover de vorderingen, aansprakelijkheden,
                            schade, verliezen en onkosten op adequate wijze zijn veroorzaakt door uw verwijtbare schending
                            van een contractuele verplichting.
                          </li>
                        </ol>
                      </li>
                      <li>
                        <span className={ TermsStyles.anchor } id="feedback">Feedback</span>
                        <ol className={ TermsStyles['sub-list'] }>
                          <li>
                            We verwelkomen en moedigen u aan om feedback, opmerkingen en suggesties voor verbeteringen
                            van de Verhuurdiensten (<strong>&quot;Feedback&quot;</strong>) te geven.
                            U kunt Feedback geven door ons te e-mailen
                            op info@yourdesq.com. U erkent en gaat ermee akkoord dat alle Feedback het exclusieve
                            eigendom wordt van YourDesq en u draagt hierbij onherroepelijk al uw rechten, titels en belangen
                            in en op alle Feedback over aan YourDesq, inclusief alle wereldwijde patenten, auteursrechten,
                            handelsgeheimen, morele en andere eigendoms- of intellectuele eigendomsrechten daarin.
                            Op verzoek en op kosten van YourDesq, zal u documenten opstellen en verdere handelingen
                            uitvoeren zoals YourDesq redelijkerwijze kan vragen om YourDesq te helpen haar intellectuele
                            eigendomsrechten en andere wettelijke bescherming voor de Feedback te verwerven, te
                            perfectioneren en te behouden.

                          </li>
                        </ol>
                      </li>
                      <li>
                        <span className={ TermsStyles.anchor } id="applicable_law">Toepasselijk recht en bevoegde rechter</span>
                        <ol className={ TermsStyles['sub-list'] }>
                          <li>
                            Op de overeenkomst is Nederlands recht van toepassing.
                          </li>
                          <li>
                            Voor zover door regels van dwingend recht niet anders wordt voorgeschreven, zullen alle
                            geschillen die mochten ontstaan naar aanleiding van de overeenkomst worden voorgelegd aan
                            de bevoegde Nederlandse rechter in het arrondissement waar YourDesq gevestigd is.
                          </li>
                          <li>
                            Indien een bepaling in deze Algemene Voorwaarden nietig blijkt te zijn, tast dit niet de
                            geldigheid van de gehele Algemene Voorwaarden aan. Partijen zullen in dat geval ter vervanging
                            (een) nieuwe bepaling(en) vaststellen, waarmee zoveel als rechtens mogelijk is aan de bedoeling
                            van de oorspronkelijke bepaling gestalte wordt gegeven.
                          </li>
                          <li>
                            Onder &quot;schriftelijk&quot; wordt in deze Algemene Voorwaarden ook communicatie per e-mail en
                            fax verstaan, mits de identiteit van de afzender en de integriteit van de e-mail voldoende vaststaat.
                          </li>
                        </ol>
                      </li>
                      <li>
                        <span className={ TermsStyles.anchor } id="other_provisions">Overige bepalingen</span>
                        <ol className={ TermsStyles['sub-list'] }>
                          <li>
                            Onze Gebruikers Restitutie Beleid, Content Beleid, Non-discriminatiebeleid, Verzachtende
                            Omstandigheden Beleid, en ander aanvullend beleid en voorwaarden waar in deze Algemene Voorwaarden
                            naar wordt verwezen, zijn van toepassing op uw gebruik van het YourDesq Platform en maken deel
                            uit van uw overeenkomst met YourDesq.
                          </li>
                          <li>
                            Behalve indien aangevuld met aanvullende voorwaarden, beleid, richtlijnen, standaarden en
                            productinformatie, vormen deze Algemene Voorwaarden de gehele overeenkomst tussen YourDesq
                            en u met betrekking tot uw toegang tot of gebruik van het YourDesq Platform en vervangen
                            alle voorgaande mondelinge of schriftelijke afspraken of overeenkomsten tussen YourDesq en u.
                            Deze Algemene Voorwaarden zijn niet bedoeld om rechten of rechtsmiddelen te verlenen aan
                            iemand anders dan u en YourDesq. Indien een bepaling van deze Algemene Voorwaarden ongeldig
                            of niet-afdwingbaar wordt geacht, zal een dergelijke bepaling worden geschrapt en zal deze
                            geen invloed hebben op de geldigheid en afdwingbaarheid van de overige bepalingen.
                          </li>
                          <li>
                            Het niet afdwingen door YourDesq van enig recht of bepaling in deze Algemene Voorwaarden vormt
                            geen verklaring van afstand van een dergelijk recht of bepaling, tenzij dit schriftelijk
                            door ons is erkend en overeengekomen. Behalve zoals uitdrukkelijk uiteengezet in deze
                            Algemene Voorwaarden, zal de uitoefening door een partij van een van haar rechtsmiddelen
                            onder deze Algemene Voorwaarden geen afbreuk doen aan haar andere rechtsmiddelen onder deze
                            Algemene Voorwaarden of anderszins toegestaan onder de wet.
                          </li>
                          <li>
                            U mag deze overeenkomst of uw rechten en verplichtingen hieruit niet toewijzen, overdragen
                            of delegeren zonder voorafgaande schriftelijke toestemming van YourDesq. YourDesq mag
                            deze overeenkomst en alle rechten en verplichtingen op grond hiervan zonder beperking
                            toewijzen, overdragen of delegeren, naar eigen goeddunken, met een voorafgaande kennisgeving
                            van dertig (30) dagen. Uw recht om deze overeenkomst op elk moment te beëindigen op grond
                            van Artikel 14 blijft onaangetast.
                          </li>
                          <li>
                            Het YourDesq Platform kan links bevatten naar websites, applicaties, diensten of bronnen van
                            derden (<strong>&quot;Diensten van derden&quot;</strong>) die onderhevig zijn aan andere voorwaarden en privacyregels.
                            YourDesq is niet verantwoordelijk of aansprakelijk voor enig aspect van dergelijke Diensten
                            van derden en links naar dergelijke Diensten van derden kunnen niet worden beschouwd als goedkeuring.
                          </li>
                          <li>
                            Sommige vertalingen op het YourDesq Platform worden aangestuurd door Google.
                            Google wijst alle garanties met betrekking tot de vertalingen, expliciet of impliciet,
                            met inbegrip van garanties voor de nauwkeurigheid, betrouwbaarheid, en alle impliciete
                            garanties voor verkoopbaarheid, geschiktheid voor een bepaald doel en niet-inbreuk af.
                            Sommige delen van het YourDesq Platform implementeren Google Maps/Earth kaarten, inclusief
                            Google Maps API(s). Uw gebruik van Google Maps/Earth is onderworpen aan de aanvullende
                            servicevoorwaarden van Google Maps/Google Earth.
                          </li>
                          <li>
                            YourDesq is niet aansprakelijk voor enige vertraging of tekortkoming in de nakoming als gevolg van oorzaken
                            buiten haar redelijke controle, met inbegrip van, maar niet beperkt tot, overmacht, oorlog, terrorisme,
                            rellen, embargo&lsquo;s, handelingen van civiele of militaire autoriteiten, brand, overstromingen, ongevallen,
                            epidemieën of ziekte, stakingen of tekorten aan transportwerklocaties, brandstof, energie, arbeid of materialen.
                          </li>
                          <li>
                            U ontvangt administratieve berichten van ons via het e-mailadres of andere contactinformatie die
                            u hebt opgegeven voor u YourDesq Account. Inschrijving op aanvullende e-mailprogramma&lsquo;s heeft
                            geen invloed op de frequentie van deze administratieve e-mails, maar u kunt verwachten dat u
                            aanvullende e-mails ontvangt die specifiek zijn voor het programma of de programma&lsquo;s waarop
                            u bent ingeschreven. U kunt ook promotie-e-mails van ons ontvangen. Voor deze promotie-e-mails
                            worden geen kosten in rekening gebracht, maar de datatarieven van derden kunnen van toepassing
                            zijn. U kunt bepalen of u promotie-e-mails ontvangt via de meldingsvoorkeuren in uw
                            accountinstellingen. Houd er rekening mee dat u niet in staat zult zijn om te profiteren van
                            bepaalde promoties als u bepaalde communicatie-instellingen uitschakelt of geen YourDesq Account hebt.
                          </li>
                          <li>
                            Als u vragen hebt over deze Algemene Voorwaarden, stuur dan een e-mail naar
                            <a href="mailto:hello@yourdesq.com">hello@yourdesq.com</a>.
                          </li>
                        </ol>
                      </li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Section>

      </main>
    </>
  );
}

TermsAndConditions.Layout = AppHeaderLayout;
